import { hourMs } from "verdi-common/timeConstants";
import { queryClient } from "@/api/queryClient";
import type { ResolutionLabel } from "../resolution/pick-resolution";
import { type CoveredRange, coveredRange } from "../stream-cache/covered-range";
import { getMissingRanges } from "../stream-cache/get-missing-ranges";
import { getStreamCacheKey, useStreamCacheStore } from "../stream-cache/stream-cache-store";
import { fetchSingleStreamData } from "./fetch-single-stream-data";
import type { FetchedStream, RequestedStream } from "./types";

// Keep re-fetching a live window for this long after its end, to catch late-arriving points.
// Past that, treat it as sealed — never fetch again.
const SEAL_DELAY_MS = 1 * hourMs; // 1 hour

// Two panels fetching the same stream+gap share one in-flight promise, via TanStack's built-in
// request dedup. staleTime/gcTime are 0 because useStreamCacheStore is the real cache: we want
// pure in-flight dedup, not a second cache layer that would suppress live-window re-fetches.
function dedupedFetch(streamId: number, resolution: ResolutionLabel, gap: CoveredRange): Promise<FetchedStream> {
    return queryClient.fetchQuery({
        queryKey: ["stream-data", streamId, resolution, gap.startMs, gap.endMs],
        queryFn: () => fetchSingleStreamData(streamId, gap.startMs, gap.endMs),
        staleTime: 0,
        gcTime: 0,
    });
}

/** For each stream: find the coverage gap, fetch it, merge into cache. See README.md. */
export async function ensureStreamData({
    streams,
    startMs,
    endMs,
    resolution,
    now,
    signal,
}: {
    streams: RequestedStream[];
    startMs: number;
    endMs: number;
    resolution: ResolutionLabel;
    now: number;
    /** When aborted, completed fetches are discarded — stale merges must not regress the trailing edge. */
    signal?: AbortSignal;
}): Promise<void> {
    // Guard against startMs >= endMs — nothing to fetch.
    if (endMs <= startMs) {
        return;
    }

    // Snapshot for gap detection only — stale after await; merge uses fresh state inside mergeIntoEntry.
    const { streamCache, mergeIntoEntry } = useStreamCacheStore.getState();

    await Promise.all(
        streams.map(async (stream) => {
            const { streamId } = stream;
            const entry = streamCache[getStreamCacheKey(streamId, resolution)];
            const gap = getMissingRanges(entry?.coveredRanges ?? [], startMs, endMs);

            if (!gap) {
                return;
            }

            let data: FetchedStream;
            try {
                data = await dedupedFetch(streamId, resolution, gap);
            } catch {
                // Don't record coverage on failure — next call will retry this gap.
                return;
            }

            const cursor = data.lastOccurredAt ? data.lastOccurredAt.getTime() : null;
            const range = coveredRange(gap.startMs, gap.endMs, cursor, now, SEAL_DELAY_MS);

            // Live window with no data yet — don't seal, try again on next input change.
            if (!range) {
                return;
            }

            if (signal?.aborted) {
                return;
            }

            mergeIntoEntry({
                streamId,
                resolution,
                points: data.points,
                coveredRange: range,
            });
        }),
    );
}
